import { Injectable, OnDestroy } from '@angular/core';
import documentFundQuery from '@graphql/fund-document/fund-documents.graphql';
import { LiteratureListingBaseService } from '@literature/literature-document-listing/services/literature-listing-base.service';
import { CartHandlerService } from '@literature/services/cart-handler.service';
import { LiteratureDataService } from '@literature/services/literature-data.service';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import { Product } from '@models';
import { HeaderDocumentsService } from '@products/fund-header/services/header-documents.service';
import { FundOverviewService } from '@products/services/fund-overview.service';
import {
  PersonalisationAPIService,
  ProfileService,
  SegmentService,
} from '@services';
import { TranslateService } from '@shared/translate/translate.service';
import {
  DocumentMatchCondition,
  LiteratureData,
  LiteratureDocument,
  LiteratureDocumentListingData,
  ProductDetailConfiguration,
  TabKey,
} from '@types';
import {
  FUND_COMMENTARY,
  MONTHLY_COMMENTARY,
  QUARTERLY_COMMENTARY,
} from '@utils/app.constants';
import { Logger } from '@utils/logger';
import cloneDeep from 'lodash/cloneDeep';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';

const logger = Logger.getLogger('LiteratureFundDocumentService');

@Injectable({
  providedIn: 'root',
})
export class LiteratureFundDocumentService
  extends LiteratureListingBaseService
  implements OnDestroy {
  constructor(
    protected literatureDataService: LiteratureDataService,
    private overviewService: FundOverviewService,
    private headerDocumentsService: HeaderDocumentsService,
    protected segmentService: SegmentService,
    protected cartService: CartHandlerService,
    protected profileService: ProfileService,
    protected personalisationService: PersonalisationAPIService,
    protected literatureConfigService: LiteratureGlobalConfigService,
    protected translateService: TranslateService
  ) {
    super(
      literatureDataService,
      literatureConfigService,
      cartService,
      segmentService,
      profileService,
      personalisationService,
      translateService
    );
  }
  documentTypes = new Map();
  dateFormat;
  paramMap: Map<string, string>;

  public populate(productDetailConfiguration: ProductDetailConfiguration) {
    if (this.isInternational) {
      this.overviewService
        .register(documentFundQuery, {
          fundId: productDetailConfiguration.fundId,
          shareClassCode: productDetailConfiguration.shareClassCode,
        })
        .pipe(
          switchMap((data: Product) => {
            this.productData(data);
            this.setParamMap(productDetailConfiguration, data);
            return this.literatureDataService
              .loadData(this.paramMap, true)
              .pipe(
                map((litData) => {
                  const combinedData: [Product, LiteratureData] = [
                    data,
                    litData,
                  ];

                  const documentsWithPrewiew: string[] =
                    this.literatureConfigService?.literatureConfig
                      .documentsWithPreview || [];
                  this.headerDocumentsService.updatePreviewConfig(
                    documentsWithPrewiew
                  );
                  this.mergeDataAndSetToAssociatedServices(combinedData);
                })
              );
          })
        )
        .subscribe();
    } else {
      this.literatureDataService
        .getProfileData$()
        ?.pipe(
          takeUntil(this.unsubscribe$),
          switchMap((data) => {
            this.literatureDataService.setProfileData(data);
            const productData$ = this.overviewService.register(
              documentFundQuery,
              {
                fundId: productDetailConfiguration.fundId,
                shareClassCode: productDetailConfiguration.shareClassCode,
              }
            );
            this.paramMap = new Map();
            this.paramMap
              .set('cache', 'true')
              .set('frkOneTISNumber', productDetailConfiguration.fundId)
              .set('shareClass', productDetailConfiguration.shareClassCode);

            const litData$ = this.literatureDataService.loadData(
              this.paramMap,
              true
            );
            return forkJoin([productData$, litData$]);
          })
        )
        .subscribe(
          (data) => {
            const [productData, litData] = data;
            // TODO - this code doesn't look correct. We should use foreach, not map for tabs
            this.productData(productData);

            const documentsWithPrewiew: string[] =
              this.literatureConfigService?.literatureConfig
                .documentsWithPreview || [];
            this.headerDocumentsService.updatePreviewConfig(
              documentsWithPrewiew
            );

            this.mergeDataAndSetToAssociatedServices(data);
          },
          (err) => {
            if (err?.status === 404) {
              logger.warn(
                '[populate] ' +
                  err?.messaage +
                  ' code: ' +
                  err?.error?.error?.errorList[0]?.code
              );
            }
          }
        );
    }
  }

  public getDocs$(
    productDetailConfiguration: ProductDetailConfiguration
  ): Observable<LiteratureDocumentListingData[]> {
    if (this.isInternational) {
      return this.getInternationalDocuments$(productDetailConfiguration);
    } else {
      return this.getNonInternationalDocuments$(productDetailConfiguration);
    }
  }

  private getNonInternationalDocuments$(
    productDetailConfiguration: ProductDetailConfiguration
  ): Observable<LiteratureDocumentListingData[]> {
    return this.overviewService
      .register(documentFundQuery, {
        fundId: productDetailConfiguration.fundId,
        shareClassCode: productDetailConfiguration.shareClassCode,
      })
      .pipe(
        switchMap((data: Product) => {
          this.productData(data);
          this.setParamMap(productDetailConfiguration, data);

          return this.literatureDataService
            .loadData(this.paramMap, true, true)
            .pipe(
              map((litData) => {
                const combinedData: [Product, LiteratureData] = [data, litData];
                return this.mergeAndReturnListingData(combinedData);
              }),
              // Do nothing with the error itself
              // just return and empty array to allow other services and components to handle this as if there are no docs for a fund
              catchError((err, caught) => {
                return of([]);
              })
            );
        })
      );
  }

  private getInternationalDocuments$(
    productDetailConfiguration: ProductDetailConfiguration
  ): Observable<LiteratureDocumentListingData[]> {
    return this.overviewService
      .register(documentFundQuery, {
        fundId: productDetailConfiguration.fundId,
        shareClassCode: productDetailConfiguration.shareClassCode,
      })
      .pipe(
        switchMap((data: Product) => {
          this.productData(data);
          this.setParamMap(productDetailConfiguration, data);
          return this.literatureDataService.loadData(this.paramMap, true).pipe(
            map((litData) => {
              const combinedData: [Product, LiteratureData] = [data, litData];
              return this.mergeAndReturnListingData(combinedData);
            }),
            catchError((err, caught) => {
              // Do nothing with the error itself
              // just return and empty array to allow other services and components to handle this as if there are no docs for a fund
              return of([]);
            })
          );
        })
      );
  }

  private setParamMap(
    productDetailConfiguration: ProductDetailConfiguration,
    data: Product
  ) {
    this.paramMap = new Map();
    this.paramMap
      .set('cache', 'true')
      .set('frkOneTISNumber', productDetailConfiguration.fundId)
      .set('shareClass', productDetailConfiguration.shareClassCode);

    if (this.isInternational) {
      this.paramMap.set('fundUmbrella', data.fundUmbrella);
    }
  }

  getColumnToDisplay(): string[] {
    return this.literatureConfigService.literatureConfig
      ?.fundDocumentGridColumn;
  }

  // reads array of documents to be displayed with 'lock'/require login
  getLoggedInColumnToDisplay(): string[] {
    return this.literatureConfigService?.literatureConfig
      ?.documentTypeDisplayForLoggedIn;
  }

  /**
   * helper method to not duplicate code
   * @param key object defining tab label and document type
   */
  private mapKeyToTabs(key: TabKey): void {
    // This is dependency for fundheader component
    // it prevens 'HEADER-DOCUMENTS' tab from being displayed and sends data to headerService
    if (key.documentTab !== 'HEADER-DOCUMENTS') {
      this.documentTypes.set(key.documentTab, key.documentTypeDisplay);
    }

    if (key.documentTab === 'HEADER-DOCUMENTS') {
      this.headerDocumentsService.updateConfig(key.documentTypeDisplay);
    }
  }
  /**
   * mergeData from literature and product data services
   * @param data data received from literature service
   */
  private mergeDataAndSetToAssociatedServices(data): void {
    const listingData = this.mergeAndReturnListingData(data);
    // send documents to header-documents service to use in other components.
    this.headerDocumentsService.updateDocuments(listingData);
    this.dataDisplayByTab(listingData);
  }

  private mergeAndReturnListingData(data): LiteratureDocumentListingData[] {
    logger.debug('merge data for literature fund document : ', data);
    this.loggedInColumnToDisplay = this.getLoggedInColumnToDisplay();
    const listingData: LiteratureDocumentListingData[] = [];
    const [fundListing, litData]: [Product, LiteratureData] = data;
    this.dateFormat = this.literatureConfigService?.dateFormat;
    const productData = fundListing;
    //  new flow logic to check map

    for (const literatureData of litData.document) {
      data = this.literatureDataService.literatureMapper(literatureData);

      // WDE-441 flag usePreviewLink informs components to redirect user to preview page instead download.
      data.usePreviewLink = this.literatureConfigService?.literatureConfig?.documentsWithPreview?.includes(
        data.dctermsType
      );
      const matchOn = this.literatureConfigService
        .getDocumentMappingConfiguration()
        .get(data.dctermsType);
      this.loadShareClassData(
        cloneDeep(data),
        productData,
        listingData,
        !(matchOn === DocumentMatchCondition.FUND_ID)
      );
    }
    return listingData;
  }

  private loadShareClassData(
    litData: LiteratureDocument,
    ppssData: any,
    listingData: LiteratureDocumentListingData[],
    showShareClass: boolean
  ): void {
    this.checkShareClassAvailablity(litData, ppssData, showShareClass);
    if (this.isValidDocument(litData)) {
      this.loadDocumentList(litData, ppssData, listingData);
    }
  }

  // to pass other document type and commentary document with recent literature document
  private isValidDocument(litData: LiteratureDocument): boolean {
    return !(
      this.checkCommentaryDocument(litData?.dctermsType) &&
      litData.isHistoryData
    );
  }

  private checkCommentaryDocument(dctermType: string): boolean {
    const commentaryDocTypes = [
      MONTHLY_COMMENTARY,
      QUARTERLY_COMMENTARY,
      FUND_COMMENTARY,
    ];
    return commentaryDocTypes.includes(dctermType);
  }

  /**
   * splits data into tabs/columns
   * @param data LiteratureDocumentListingData array
   */
  private dataDisplayByTab(data: LiteratureDocumentListingData[]) {
    const dataTab = new Map();
    for (const [tab, docTypes] of this.documentTypes.entries()) {
      data.forEach((element) => {
        if (docTypes?.includes(element.literatureData.dctermsType?.trim())) {
          if (dataTab.has(tab)) {
            const documents = dataTab.get(tab);
            documents.push(element);
            dataTab.set(tab, documents);
          } else {
            dataTab.set(tab, [element]);
          }
        }
      });
    }
    this.state.datatab = this.sortDataByConfig(dataTab);
    this.documentListingData$.next(this.state);
  }

  /**
   * Below method will sort the document data based on literature config
   */
  sortDataByConfig(dataTab) {
    let counter = 0;
    dataTab.forEach((tabData: LiteratureDocumentListingData[]) => {
      const sortOrder = this.documentTypes.get(
        Array.from(this.documentTypes.keys())[counter]
      );
      // Sort documents based on sequence available in literature config
      tabData.sort(
        (
          a: LiteratureDocumentListingData,
          b: LiteratureDocumentListingData
        ) => {
          const A: string = a.literatureData?.dctermsType;
          const B: string = b.literatureData?.dctermsType;
          if (sortOrder.indexOf(A) > sortOrder.indexOf(B)) {
            return 1;
          } else {
            return -1;
          }
        }
      );
      counter++;

      const documentGroupSortResults = tabData.reduce(
        (results: any, document: LiteratureDocumentListingData) => {
          results[document?.literatureData?.dctermsType] = [
            ...(results[document?.literatureData?.dctermsType] || []),
            document,
          ];
          // Sort each document type group alphabetically
          results[document?.literatureData?.dctermsType]?.sort(
            (
              a: LiteratureDocumentListingData,
              b: LiteratureDocumentListingData
            ) => {
              return a.literatureData?.dctermsTitle
                ?.toLocaleLowerCase()
                .localeCompare(
                  b.literatureData?.dctermsTitle?.toLocaleLowerCase()
                );
            }
          );
          return results;
        },
        Object.create(null)
      );

      const finalSortedDocuments: LiteratureDocumentListingData[] = Object.keys(
        documentGroupSortResults
      ).reduce((groupValues: any[], groupName: string) => {
        return groupValues.concat(documentGroupSortResults[groupName]);
      }, []);

      tabData.splice(0, tabData.length, ...finalSortedDocuments);
    });
    return dataTab;
  }

  productData(data: Product): void {
    this.literatureConfigService?.literatureConfig[
      data.productType
    ]?.tabs.forEach((key: TabKey) => {
      this.mapKeyToTabs(key);
    });
  }

  ngOnDestroy(): void {
    this.onDestroyUnsubscribe();
  }
}
